/**
 * Rendered by `RequireRole` when the caller lacks the required role, and by
 * `RequireAgent` when they have not been given the agent (ADR-0048). Surfaces
 * the denial reason instead of silently redirecting — operators reported that
 * a bounce to /me looked like a broken nav link rather than an authorisation
 * outcome.
 *
 * UI-only. The backend has already denied any direct API call;
 * this is the client's reflection of that decision.
 */

import { Alert, Anchor, Badge, Container, Group, Stack, Text } from "@mantine/core";
import { Link } from "react-router-dom";

interface Props {
  /** The role a page needs (`RequireRole`). */
  requiredRole?: string;
  callerRoles?: readonly string[];
  /** The agent a page belongs to (`RequireAgent`). */
  agentName?: string;
  /** The agent's Solution is not part of this deployment (ADR-0050):
   *  asking for access would not help, so the page says so. */
  notOffered?: boolean;
}

export function ForbiddenPage({
  requiredRole,
  callerRoles = [],
  agentName,
  notOffered = false,
}: Props) {
  if (agentName && notOffered) {
    return (
      <Container size="sm" py="xl">
        <Alert variant="light" color="gray" title="Not available" role="alert">
          <Stack gap="sm">
            <Text size="sm" span>
              <Badge color="gray" variant="light" component="span">
                {agentName}
              </Badge>{" "}
              is not available in this deployment.
            </Text>
            <Text size="sm">
              <Anchor component={Link} to="/">
                Return to Home
              </Anchor>
              .
            </Text>
          </Stack>
        </Alert>
      </Container>
    );
  }
  return (
    <Container size="sm" py="xl">
      <Alert variant="light" color="red" title="Access denied" role="alert">
        <Stack gap="sm">
          {agentName ? (
            <Text size="sm" span>
              You do not have access to{" "}
              <Badge color="red" variant="light" component="span">
                {agentName}
              </Badge>
              . Ask an administrator, or the Developer who looks after this Solution, to give you
              access.
            </Text>
          ) : (
            <>
              <Text size="sm" span>
                This page requires the{" "}
                <Badge color="red" variant="light" component="span">
                  {requiredRole}
                </Badge>{" "}
                role. Your account does not currently have it.
              </Text>
              <Group gap="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Your roles
                </Text>
                {callerRoles.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    (none)
                  </Text>
                ) : (
                  callerRoles.map((r) => (
                    <Badge key={r} color="gray" variant="light">
                      {r}
                    </Badge>
                  ))
                )}
              </Group>
            </>
          )}
          <Text size="sm">
            If you believe this is wrong, contact a tenant administrator. Otherwise{" "}
            <Anchor component={Link} to="/">
              return to Home
            </Anchor>
            .
          </Text>
        </Stack>
      </Alert>
    </Container>
  );
}
